import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { mockCases, activityLog } from '@/mocks/cases';
import { useAuth } from '@/context/AuthContext';

const riskColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  moderate: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

const statusColors: Record<string, string> = {
  active: 'bg-teal-100 text-teal-700',
  pending_review: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-100 text-gray-600',
  archived: 'bg-gray-100 text-gray-500',
  referred: 'bg-blue-100 text-blue-700',
};

const activityIcons: Record<string, string> = {
  assessment: 'ri-shield-check-line',
  create: 'ri-file-add-line',
  review: 'ri-eye-line',
  referral: 'ri-share-forward-line',
  document: 'ri-file-upload-line',
  approval: 'ri-checkbox-circle-line',
  close: 'ri-archive-line',
};

const activityColors: Record<string, string> = {
  assessment: 'text-teal-500 bg-teal-50',
  create: 'text-green-500 bg-green-50',
  review: 'text-amber-500 bg-amber-50',
  referral: 'text-blue-500 bg-blue-50',
  document: 'text-purple-500 bg-purple-50',
  approval: 'text-teal-500 bg-teal-50',
  close: 'text-gray-500 bg-gray-50',
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [lastSync, setLastSync] = useState(new Date().toLocaleTimeString());

  useEffect(() => {
    const interval = setInterval(() => {
      setLastSync(new Date().toLocaleTimeString());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const totalActive = mockCases.filter((c) => c.status === 'active').length;
  const criticalCases = mockCases.filter((c) => c.riskLevel === 'critical').length;
  const pendingReview = mockCases.filter((c) => c.supervisorApproval === 'pending').length;
  const closedThisMonth = mockCases.filter((c) => c.status === 'closed').length;

  const filteredCases = activeFilter === 'all'
    ? mockCases
    : mockCases.filter((c) => c.status === activeFilter || c.riskLevel === activeFilter);

  const urgentCases = mockCases.filter((c) => c.riskLevel === 'critical' || c.riskLevel === 'high');

  return (
    <AppLayout title="Dashboard & Monitoring" breadcrumb={['Home', 'Dashboard']}>
      {/* Real-time indicator */}
      <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 px-3 py-2 rounded-xl mb-4 w-fit">
        <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse flex-shrink-0" />
        <span>Live Dashboard — Auto-updates every 5 seconds · Last sync: {lastSync}</span>
      </div>

      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-6 mb-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-1">Good morning, {user?.name?.split(' ')[0]}!</h2>
            <p className="text-teal-100 text-sm">
              You have <strong>{urgentCases.length} urgent cases</strong> requiring attention today.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={() => navigate('/case-intake')}
              className="bg-white text-teal-600 font-semibold text-sm px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1" />
              New Case
            </button>
            <button className="bg-teal-400/30 text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-teal-400/50 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-download-line mr-1" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Active Cases', value: totalActive, icon: 'ri-folder-open-line', color: 'text-teal-500', bg: 'bg-teal-50', trend: '+2 this week' },
          { label: 'Critical Risk', value: criticalCases, icon: 'ri-alarm-warning-line', color: 'text-red-500', bg: 'bg-red-50', trend: 'Immediate action' },
          { label: 'Pending Review', value: pendingReview, icon: 'ri-time-line', color: 'text-amber-500', bg: 'bg-amber-50', trend: 'Awaiting supervisor' },
          { label: 'Closed This Month', value: closedThisMonth, icon: 'ri-checkbox-circle-line', color: 'text-green-500', bg: 'bg-green-50', trend: 'Successfully resolved' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-start justify-between mb-3">
              <div className={`w-10 h-10 ${kpi.bg} rounded-xl flex items-center justify-center`}>
                <i className={`${kpi.icon} ${kpi.color} text-lg`} />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900 mb-1">{kpi.value}</p>
            <p className="text-sm font-medium text-gray-700 mb-1">{kpi.label}</p>
            <p className="text-xs text-gray-400">{kpi.trend}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Case List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-semibold text-gray-800">Case Overview</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {['all', 'active', 'critical', 'pending_review'].map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap capitalize
                    ${activeFilter === f ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {f.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left font-medium">Case No.</th>
                  <th className="px-5 py-3 text-left font-medium">Child</th>
                  <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Incident</th>
                  <th className="px-5 py-3 text-left font-medium">Risk</th>
                  <th className="px-5 py-3 text-left font-medium">Status</th>
                  <th className="px-5 py-3 text-left font-medium">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredCases.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-xs font-mono text-gray-600">{c.caseNumber.split('-').slice(-1)[0]}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{c.childName}</p>
                        <p className="text-xs text-gray-400">{c.age} yrs • {c.gender}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <span className="text-xs text-gray-600">{c.incidentType}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full border font-medium capitalize ${riskColors[c.riskLevel]}`}>
                        {c.riskLevel}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${statusColors[c.status]}`}>
                        {c.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => navigate(`/child-profiles/${c.childId}`)}
                        className="text-teal-500 hover:text-teal-700 text-xs font-medium cursor-pointer whitespace-nowrap"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Urgent Alerts */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <i className="ri-alarm-warning-line text-red-500" />
              <h3 className="font-semibold text-gray-800 text-sm">Urgent Alerts</h3>
              <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {urgentCases.length}
              </span>
            </div>
            <div className="divide-y divide-gray-50">
              {urgentCases.map((c) => (
                <div key={c.id} className="px-5 py-3 flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${c.riskLevel === 'critical' ? 'bg-red-500' : 'bg-orange-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{c.childName}</p>
                    <p className="text-xs text-gray-500">{c.incidentType}</p>
                    <p className="text-xs text-gray-400">{c.municipality}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/child-profiles/${c.childId}`)}
                    className="text-teal-500 text-xs cursor-pointer whitespace-nowrap"
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex-1">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">Recent Activity</h3>
            </div>
            <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
              {activityLog.map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-start gap-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${activityColors[a.type]}`}>
                    <i className={`${activityIcons[a.type]} text-xs`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 leading-relaxed">{a.action}</p>
                    <p className="text-xs text-gray-400 font-mono">{a.caseNumber.split('-').slice(-1)[0]}</p>
                    <p className="text-xs text-gray-400">{a.timestamp} · {a.user}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Supervisor Pending Approvals */}
      {user?.role !== 'social_worker' && (
        <div className="mt-6 bg-white rounded-xl border border-amber-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-100 flex items-center gap-2 bg-amber-50">
            <i className="ri-time-line text-amber-500" />
            <h3 className="font-semibold text-amber-800 text-sm">Pending Supervisor Approvals</h3>
            <span className="ml-auto bg-amber-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
              {pendingReview}
            </span>
          </div>
          <div className="divide-y divide-gray-50">
            {mockCases.filter((c) => c.supervisorApproval === 'pending').map((c) => (
              <div key={c.id} className="px-5 py-4 flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{c.childName} — {c.caseNumber}</p>
                  <p className="text-xs text-gray-500">{c.incidentType} · {c.municipality} · Assigned to {c.assignedWorker}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/child-profiles/${c.childId}`)}
                    className="text-xs bg-teal-500 text-white px-3 py-1.5 rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Review
                  </button>
                  <button className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap">
                    Approve
                  </button>
                  <button className="text-xs bg-red-100 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-200 transition-colors cursor-pointer whitespace-nowrap">
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
